<template>
	<v-overlay
		v-if="showOverlay && !dismissed"
		:value="true"
		opacity="0"
		z-index="9999"
		class="status-overlay"
		:class="statusClass"
	>
		<div class="overlay-content">
			<div class="status-icon-container" :class="statusClass">
				<v-icon size="64" color="white">{{ statusIcon }}</v-icon>
			</div>

			<h1 class="overlay-title">
				{{ title }}
			</h1>

			<p v-if="status.bannerText" class="overlay-message">
				{{ status.bannerText }}
			</p>

			<div v-if="status.maintenanceRemaining" class="countdown-container">
				<v-icon color="white" class="mr-2">mdi-clock-outline</v-icon>
				<span class="countdown-text">
					{{ $t("estimated-time-remaining") }}: {{ status.maintenanceRemaining }} {{ $t("minutes") }}
				</span>
			</div>

			<v-progress-linear
				indeterminate
				rounded
				height="6"
				color="white"
				class="progress-bar"
			/>

			<p class="overlay-hint">
				{{ $t("overlay-wait-message") }}
			</p>

			<v-btn
				v-if="isDevops"
				text
				class="devops-dismiss-btn mt-4"
				@click="dismissed = true"
			>
				<v-icon left small>mdi-shield-account</v-icon>
				{{ $t("devops-bypass") }}
			</v-btn>
		</div>
	</v-overlay>
</template>

<script>
const STATUS_CONFIG = {
	operational: { icon: "mdi-check-circle", title: "status-operational" },
	maintenance: { icon: "mdi-wrench", title: "status-maintenance-title" },
	degraded: { icon: "mdi-speedometer-slow", title: "status-degraded-title" },
	partial_outage: { icon: "mdi-alert", title: "status-partial-title" },
	major_outage: { icon: "mdi-alert-octagon", title: "status-major-title" }
};

export default {
	name: "StatusOverlay",

	props: {
		status: {
			type: Object,
			required: true
		},

		isDevops: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			dismissed: false
		};
	},

	computed: {
		showOverlay() {
			return this.status?.enabled && this.status?.effectiveOverlay;
		},

		statusClass() {
			return `status-${this.status?.status || "maintenance"}`;
		},

		statusIcon() {
			const config = STATUS_CONFIG[this.status?.status] || STATUS_CONFIG.maintenance;

			return config.icon;
		},

		title() {
			const config = STATUS_CONFIG[this.status?.status] || STATUS_CONFIG.maintenance;

			return this.$t(config.title);
		}
	}
};
</script>

<style scoped>
.status-overlay {
	background: linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #01579b 100%);
}

.status-overlay.status-maintenance {
	background: linear-gradient(135deg, #1a1a4e 0%, #3C41C3 50%, #2e3199 100%);
}

.status-overlay.status-degraded {
	background: linear-gradient(135deg, #5c4600 0%, #FFC107 50%, #FFA000 100%);
}

.status-overlay.status-partial_outage {
	background: linear-gradient(135deg, #4a1c0d 0%, #ff5722 50%, #e64a19 100%);
}

.status-overlay.status-major_outage {
	background: linear-gradient(135deg, #4a0d0d 0%, #FF5252 50%, #FF1744 100%);
}

.status-overlay.status-operational {
	background: linear-gradient(135deg, #1b3d1b 0%, #4caf50 50%, #388e3c 100%);
}

.overlay-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 40px;
	max-width: 500px;
}

.status-icon-container {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 120px;
	height: 120px;
	border-radius: 50%;
	margin-bottom: 24px;
	animation: pulse 2s infinite;
}

.status-icon-container.status-maintenance {
	background: linear-gradient(135deg, #3C41C3 0%, #2e3199 100%);
}

.status-icon-container.status-degraded {
	background: linear-gradient(135deg, #FFC107 0%, #FFA000 100%);
}

.status-icon-container.status-partial_outage {
	background: linear-gradient(135deg, #ff5722 0%, #e64a19 100%);
}

.status-icon-container.status-major_outage {
	background: linear-gradient(135deg, #FF5252 0%, #FF1744 100%);
}

.status-icon-container.status-operational {
	background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
}

@keyframes pulse {
	0%, 100% {
		transform: scale(1);
		box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
	}
	50% {
		transform: scale(1.05);
		box-shadow: 0 0 0 20px rgba(255, 255, 255, 0);
	}
}

.overlay-title {
	font-size: 28px;
	font-weight: 600;
	color: white;
	margin-bottom: 16px;
}

.overlay-message {
	font-size: 16px;
	color: rgba(255, 255, 255, 0.9);
	margin-bottom: 24px;
	line-height: 1.5;
}

.countdown-container {
	display: flex;
	align-items: center;
	background: rgba(255, 255, 255, 0.15);
	padding: 12px 24px;
	border-radius: 24px;
	margin-bottom: 32px;
}

.countdown-text {
	font-size: 16px;
	font-weight: 500;
	color: white;
}

.progress-bar {
	width: 200px;
	margin-bottom: 24px;
}

.overlay-hint {
	font-size: 13px;
	color: rgba(255, 255, 255, 0.6);
}

.devops-dismiss-btn {
	opacity: 0.9;
	text-transform: none;
	color: white !important;
	background: rgba(255, 255, 255, 0.15) !important;
	border: 1px solid rgba(255, 255, 255, 0.3);
}

.devops-dismiss-btn:hover {
	opacity: 1;
	background: rgba(255, 255, 255, 0.25) !important;
}
</style>
